/**
 * PortalLogin - Customer portal login via magic link
 */

import { useState } from 'react';
import { Mail, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

export function PortalLoginPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/api/portal/auth/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error('Error');
      setStatus('sent');
    } catch {
      setStatus('error');
      setErrorMsg('Error al enviar el enlace. Intente de nuevo.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          {status === 'sent' ? (
            <div className="text-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Enlace Enviado</h1>
              <p className="text-gray-500 mb-4">
                Si existe una cuenta con ese email, recibirá un enlace de acceso.
                Revise su bandeja de entrada.
              </p>
              <Button variant="outline" onClick={() => setStatus('idle')}>
                Enviar otro enlace
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="h-14 w-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-blue-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">Acceder a Mi Portal</h1>
                <p className="text-gray-500 text-sm mt-1">
                  Ingrese su email para recibir un enlace de acceso
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="su@email.com"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={status === 'loading'}
                  />
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-600">{errorMsg}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={status === 'loading' || !email.trim()}
                >
                  {status === 'loading' ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  Enviar Enlace de Acceso
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
